import LogApi from '@/api/common/log';
import {filterEmptyParams} from '@/utils';
import {useEffect, useRef, useState} from 'react';

/**
 * 리스트 컴포넌트의 화면 노출 시 LISTVIEW 로그를 발송하는 커스텀 훅
 * Note. 정책 논의 결과 리스트가 노출되었을 때 노출된 아이템 만이 아닌 모든 아이템을 담아 로그를 보내는 것으로 결정됨
 * Note. 그 외 정책으로 노출된 아이템 만을 담아 보내는 로그를 원하는 요건이 발생하여 파라미터를 업데이트 해주는 함수를 추가
 * @param {Object} params
 * @param {string} params.path1 진입 경로 1
 * @param {string} params.path2 진입 경로 2
 * @param {Object} [params.addInfo] 추가로 전달할 로그용 파라미터 객체 (예: userId, sessionId 등)
 * @param {'LIVE' | 'VOD' | 'CATCH'} params.listType 리스트 아이템의 유형
 * @param {string} params.selector 리스트에 해당하는 Element Selector 문자열
 * @param {Array<Object>} targetItemList 리스트 아이템 객체 배열
 */

const useListViewLog = (
  {path1 = '', path2 = '', listType, selector, threshold = 0.33, ...addInfo},
  targetItemList,
) => {
  const [listViewParam, setListViewParam] = useState({
    path1,
    path2,
    ...addInfo, // 여기에 같이 포함시켜서 로그 파라미터에 쓸 수 있게
  });

  const [listViewList, setListViewList] = useState(targetItemList);

  // 최신 상태 유지용 ref
  const listViewParamRef = useRef(listViewParam);
  // targetItemList 최신 값 보존용 ref
  const targetItemListRef = useRef(listViewList);

  // ref 최신화: 파라미터
  useEffect(() => {
    listViewParamRef.current = listViewParam;
  }, [listViewParam]);

  // ref 최신화: 리스트
  useEffect(() => {
    if (listViewList) {
      targetItemListRef.current = listViewList;
    }
  }, [listViewList]);

  /**
   * 로그 발송 함수
   */
  const sendLog = async () => {
    const currentParam = listViewParamRef.current;
    const currentTargetItemList = targetItemListRef.current;
    const params = {
      path1: currentParam.path1,
      path2: currentParam.path2,
      listSendType: 'client',
      listViewCnt: currentTargetItemList.length,
      ...filterEmptyParams(currentParam), // 빈값 필터링해서 병합
    };
    try {
      switch (listType) {
        case 'LIVE':
          params.listViewData = currentTargetItemList.map((item) => ({
            bno: item.broadcastId,
            ...(item.listDataType && {list_data_type: item.listDataType}),
          }));
          await LogApi.sendLiveListViewLog(params);
          break;
        case 'VOD':
          params.listViewData = currentTargetItemList.map((item) => ({
            vno: item.broadcastId,
          }));
          await LogApi.sendVodListViewLog(params);
          break;
        case 'CATCH':
          params.listViewData = currentTargetItemList.map((item) => ({
            vno: item.catchId,
            listDataType: item.listDataType,
          }));
          await LogApi.sendVodListViewLog(params);
          break;
        default:
      }
    } catch (error) {
      throw error;
    }
  };

  /**
   * 인터섹션 관찰자 객체
   */
  const observerRef = useRef(null);

  /** 대상으로 지정된 엘리먼트의 노출 여부를 관찰 시작 */
  /** 대상으로 지정된 엘리먼트의 노출 여부를 관찰 시작 (Promise 반환) */
  const startObserve = () => {
    return new Promise((resolve, reject) => {
      observerRef.current = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            sendLog()
              .then(() => {
                observerRef.current.disconnect(); // 로그는 한 번만 전송
                resolve();
              })
              .catch((error) => {
                observerRef.current.disconnect(); // 실패해도 옵저버 정리
                reject(error);
              });
          }
        },
        {
          threshold: threshold,
        },
      );

      const targetElement = document.querySelector(selector);
      if (targetElement) {
        observerRef.current.observe(targetElement);
      }
    });
  };

  /** 노출 여부 관찰 종료 */
  const endObserve = () => {
    if (observerRef.current) {
      observerRef.current.disconnect();
    }
  };

  /**
   * 동적으로 변하는 로그값을 set
   */
  const updateListViewParam = (updateParam) => {
    setListViewParam((prev) => ({
      ...prev,
      ...updateParam,
    }));
  };

  /**
   * 동적으로 변하는 리스트 값을 set
   */
  const updateTargetItemList = (updateList) => {
    setListViewList(updateList);
  };

  return {
    /** 리스트 내부 방송 아이템들의 화면 노출 여부를 관찰 시작 */
    startObserve,
    /** 노출 여부 관찰 종료 */
    endObserve,
    /** 파라미터 업데이트 */
    updateListViewParam,
    updateTargetItemList,
  };
};

export default useListViewLog;
